import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms"; // Add import
import {
  CloseOutlined,
  EllipsisOutlined,
  GlobalOutlined,
  HeartFilled,
  LikeFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import { BiLike, BiShare } from "react-icons/bi";
import { FaRegComment } from "react-icons/fa";
import CaptionExpand from "./components/CaptionExpand"; // Import CaptionExpand
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";
// import TextSelectionOverlay from '../TextSelectionOverlay'; // Simplified for now
// import { useIsMobile } from '@/hooks/use-mobile'; // Simplified for now

export interface FacebookCardProps extends BaseSocialCardProps {
  title?: string;
  // captionLimit?: number; // Remove captionLimit prop
  platformId: string; // Add platformId prop
}

const FacebookCard = ({
  caption,
  mediaUrls = [],
  username = "User", // Default placeholder
  profileUrl,
  postedAt, // Use the passed prop
  // captionLimit, // Remove captionLimit destructuring
  platformId, // Destructure platformId
  isTemplateVideo,
}: FacebookCardProps) => {
  // const [expanded, setExpanded] = useState(false);
  // const [isEditing, setIsEditing] = useState(false);
  // const [selectedText, setSelectedText] = useState('');
  // const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  // const [selectionPosition, setSelectionPosition] = useState({ x: 0, y: 0 });
  // const [isEditMode, setIsEditMode] = useState(false);
  // const captionRef = useRef<HTMLParagraphElement>(null); // Remove unused ref
  // const { data: user } = useSuspenseQuery(getMeQueryOptions);

  // Fetch platform settings and caption limit
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  // Remove unused expansion logic
  // const needsExpansion = useMemo(() => { ... });

  const displayCaption = useMemo(() => {
    // Remove expansion logic, just truncate
    if (captionLimit && caption.length > captionLimit) {
      return caption.substring(0, captionLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  // Simplified: Removed overlay logic
  // const handleOverlayClose = () => { ... };
  // const handleTextUpdate = (newText: string) => { ... };

  // Remove old render function
  // const renderCaption = () => { ... };

  return (
    <div className="w-full bg-antd-colorBgContainer text-antd-colorText border border-antd-colorBorderSecondary rounded-lg overflow-hidden shadow-md font-medium">
      <style>{`
        .facebook-card-preview .hashtag {
          color: #0095F6; /* Using Instagram's Blue */
          text-decoration: none;
        }
        .facebook-card-preview .hashtag:hover {
          text-decoration: underline;
        }
        /* Links (.caption-link) remain default text color */
      `}</style>

      {/* Header */}
      <div className="px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Avatar
            size={40}
            src={profileUrl}
            icon={!profileUrl ? <UserOutlined /> : undefined}
          />
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{username}</span>
            <div className="flex items-center text-xs  text-antd-colorTextSecondary">
              <span>
                {postedAt
                  ? formatDistanceToNow(new Date(postedAt), {
                      addSuffix: true,
                    })
                  : "Recently"}
              </span>
              <span className="mx-1">•</span>
              <GlobalOutlined className="text-antd-colorTextSecondary" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-antd-colorTextSecondary">
            <EllipsisOutlined />
          </div>
          <div className="text-antd-colorTextSecondary">
            <CloseOutlined />
          </div>
        </div>
      </div>

      {/* Caption */}
      {displayCaption && (
        <div className="px-4 py-2 font-normal">
          <CaptionExpand
            text={displayCaption}
            initialLines={3} // Adjust as needed
            showMoreText="... See more"
            showLessText="...less"
            linkColor="gray"
            className="text-sm text-antd-colorText"
          />
        </div>
      )}

      <div className="flex border-t border-b border-antd-colorBorderSecondary min-h-[200px]">
        <MediaSection mediaUrls={mediaUrls} isTemplateVideo={isTemplateVideo} />
      </div>

      {/* Reactions Summary - Removed divider */}
      <div className="px-4 flex justify-between items-center  text-antd-colorTextSecondary text-sm mt-2">
        <div className="flex items-center gap-1">
          <div className="flex">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 border-2 border-antd-colorBgContainer">
              <LikeFilled style={{ fontSize: "8px", color: "white" }} />
            </span>
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 -ml-1 border-2 border-antd-colorBgContainer">
              <HeartFilled style={{ fontSize: "8px", color: "white" }} />
            </span>
          </div>
          <span>28</span>
        </div>
        <div className="flex gap-2">
          <span>18 comments</span>
        </div>
      </div>

      {/* Action Buttons  */}
      <div className="px-4 py-2 flex justify-between items-center">
        <div className="flex-1 flex justify-center items-center gap-1 text-antd-colorTextSecondary text-sm py-1 hover:bg-antd-colorFillTertiary rounded cursor-pointer">
          <BiLike style={{ fontSize: "18px" }} />
          <span>Like</span>
        </div>
        <div className="flex-1 flex justify-center items-center gap-1 text-antd-colorTextSecondary text-sm py-1 hover:bg-antd-colorFillTertiary rounded cursor-pointer">
          <FaRegComment style={{ fontSize: "18px" }} />
          <span>Comment</span>
        </div>
        <div className="flex-1 flex justify-center items-center gap-1 text-antd-colorTextSecondary text-sm py-1 hover:bg-antd-colorFillTertiary rounded cursor-pointer">
          <BiShare style={{ fontSize: "18px" }} />
          <span>Share</span>
        </div>
      </div>

      {/* Footer/More Comments (Optional) */}
      {/* <div className="px-4 py-2 text-center text-sm text-gray-500 hover:underline cursor-pointer">
          View more comments
        </div> */}
    </div>

    // Simplified: Removed TextSelectionOverlay for now
  );
};

export default FacebookCard;
